import axios from "axios";
import { getAuth } from "firebase/auth";
import { getApiUrl } from "./envConfig";
import {
  validateSecurityHeaders,
  getApiSecurityHeaders,
} from "./securityHeaders";
import {
  getAuthToken,
  refreshAuthToken,
  waitForUser,
  redirectToSessionExpired,
} from "./authUtils";

// Enhanced axios utility with token management and retry logic
export const makeAuthenticatedRequest = async (
  url,
  options = {},
  retries = 3,
  parseErrorDetail = null,
  parseTerminalErrors = false,
) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...options.headers,
    };

    const requestConfig = {
      url,
      headers,
      method: options.method || "GET",
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development
    if (import.meta.env.VITE_ENVIRONMENT === "development") {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error) {
    // No Firebase session: getAuthToken/waitForUser rejected with no HTTP
    // response because there is no signed-in user. This is a session-loss, not
    // a data failure — redirect to login with the inactivity banner instead of
    // surfacing "No user found" on whatever page fired this call. The hard
    // navigation ends this request chain (no pointless retries).
    if (!error.response && /No (user|token) found/i.test(error.message || "")) {
      redirectToSessionExpired();
      throw error;
    }
    const status = error.response?.status;
    // Surface ONLY FastAPI's `detail` string — the field contractually meant
    // to be shown to the client. Ignore non-string `detail` (422 returns an
    // array of objects, which would stringify to "[object Object]").
    const detail = error.response?.data?.detail;
    const detailMsg = typeof detail === "string" && detail ? detail : null;

    const method = (options.method || "GET").toUpperCase();
    // POST/PATCH are not idempotent: the server may have applied the change
    // before a 5xx/network failure, so blindly retrying risks a duplicate
    // side effect (e.g. a second user). Only auto-retry idempotent methods on
    // 5xx; a rejected 401 is always safe to retry (the request never ran).
    const isIdempotent = method !== "POST" && method !== "PATCH";

    // A non-401 client error (4xx) is a real, deterministic rejection — e.g.
    // a 400 "product_scopes must include at least one country", a 403, or a
    // 409 deactivated-user. A fresh token will not change any of them, so
    // surface the reason (with status, so callers can act on 409) rather than
    // retrying. Backend status semantics: apps/api/src/utils/auth.py.
    if (status >= 400 && status < 500 && status !== 401) {
      const err = new Error(
        detailMsg || "Request failed. Please check your input and try again.",
      );
      err.status = status;
      // The whole body lets a caller read fields beside `detail`, such as a
      // problem response's `code`.
      if (parseErrorDetail)
        err.detail = parseErrorDetail(detail, status, error.response?.data);
      throw err;
    }

    // Terminal: out of retries, OR a 5xx/network failure on a non-idempotent
    // request we won't retry. Surface the ACTUAL failure (backend detail /
    // axios message) — never a generic "Max retries reached" that hides the
    // real cause behind an auth-looking error.
    const nonIdempotentFailure =
      !isIdempotent && (status === undefined || status >= 500);
    if (retries <= 0 || nonIdempotentFailure) {
      const err = new Error(
        // A 401 that survived every refresh is a re-auth situation — match the
        // refresh-failure copy rather than surfacing the raw "Invalid token".
        status === 401
          ? "Your session has expired. Please sign in again."
          : detailMsg || error.message || "Request failed. Please try again.",
      );
      if (status !== undefined) err.status = status;
      if (
        parseTerminalErrors === true &&
        parseErrorDetail &&
        status !== undefined
      ) {
        err.detail = parseErrorDetail(detail, status);
      }
      throw err;
    }

    // 401 → the token was rejected: force-refresh once. A refresh failure is
    // terminal (we can't authenticate), surfaced plainly. The retry recursion
    // lives OUTSIDE this catch, so its errors are never re-wrapped as a token
    // failure (the source of the old triple-nested message).
    if (status === 401) {
      try {
        await refreshAuthToken();
      } catch {
        const err = new Error(
          "Your session has expired. Please sign in again.",
        );
        err.status = 401;
        throw err;
      }
    }

    // 401 (post-refresh) or an idempotent 5xx/network error → retry.
    return await makeAuthenticatedRequest(
      url,
      options,
      retries - 1,
      parseErrorDetail,
      parseTerminalErrors,
    );
  }
};

// Fetch user data and extract company (id_client) value
export const fetchUserData = async (withScope = true) => {
  try {
    // Wait for Firebase to restore auth session (important on page refresh!)
    const currentUser = await waitForUser();

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const userId = currentUser.uid;
    const url = `${getApiUrl()}/users/${userId}?with_scope=${withScope}`;

    const data = await makeAuthenticatedRequest(url, { timeout: 30000 }, 0);
    return data;
  } catch (error) {
    const failure = new Error("Failed to fetch user data: " + error.message);
    failure.status = error.status;
    throw failure;
  }
};

// The countries a user may open for a product as { regions, default_country }.
export const fetchUserCountries = async ({
  id_client,
  id_user,
  product_name,
}) => {
  try {
    if (!id_client) {
      throw new Error("Client ID is required");
    }

    const resolvedIdUser = id_user ?? getAuth().currentUser?.uid;
    const resolvedProductName = product_name ?? "execution_view";

    if (!resolvedIdUser) {
      throw new Error("User ID is required");
    }

    const url = `${getApiUrl()}/${id_client}/users/${resolvedIdUser}/countries?product_name=${resolvedProductName}`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    throw new Error("Failed to fetch user countries: " + error.message);
  }
};

export const CreateGoogleUserDoc = async ({ email, id_user }) => {
  try {
    const url = `${getApiUrl()}/users`;

    // Dashmote staff get workspace access from the implicit-access
    // model, not a per-workspace grant. Create an identity-only doc with an
    // empty id_client — the backend defaults default_id_client to ko_global and
    // tolerates the empty map for @dashmote.com staff. (No more hardcoded
    // ko_global / super_admin self-grant on every Google login.)
    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      timeout: 30000,
      body: JSON.stringify({
        auth_method: "google",
        email: email,
        id_user: id_user,
        id_client: {},
      }),
    });

    return data;
  } catch (error) {
    throw new Error("Could not prepare your staff account: " + error.message);
  }
};
